import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { DeleteButton } from '../components/shared/DeleteButton';

interface Secret {
  id: string;
  name: string;
  secret_id: string;
  description?: string;
  secret_type?: string;
  tags: string[];
  created_at: string;
  updated_at: string;
}

const SecretsPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, actions } = useApp();
  const [secrets, setSecrets] = useState<Secret[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadSecrets();
  }, []);

  const loadSecrets = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/secrets/');
      if (response.ok) {
        const data = await response.json();
        setSecrets(data);
      }
    } catch (error) {
      console.error('Failed to load secrets:', error);
    }
    setLoading(false);
  };

  const handleDelete = async (secretId: string) => {
    
    try {
      const response = await fetch(`/api/v1/secrets/${secretId}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        loadSecrets();
      }
    } catch (error) {
      console.error('Failed to delete secret:', error);
    }
  };

  const filteredSecrets = secrets.filter(secret => {
    const trimmedSearch = searchTerm.trim();
    if (!trimmedSearch) return true;
    
    const searchLower = trimmedSearch.toLowerCase();
    return (
      secret.name.toLowerCase().includes(searchLower) ||
      secret.description?.toLowerCase().includes(searchLower) ||
      secret.secret_id.toLowerCase().includes(searchLower) ||
      secret.tags.some(tag => tag.toLowerCase().includes(searchLower))
    );
  });

  return (
    <div className="container-fluid">
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => window.history.back()}
          >
            <i className="fas fa-times"></i> Close
          </button>
          <div style={{ position: 'relative', display: 'inline-block', marginLeft: '0.75rem' }}>
            <i className="fas fa-search" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: '0.875rem', pointerEvents: 'none' }}></i>
            <input
              type="text"
              className="form-control"
              placeholder="Filter Secrets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '240px', paddingLeft: '2.25rem', backgroundColor: 'rgba(255, 255, 255, 0.3)', border: 'none', fontSize: '0.875rem', outline: 'none', boxShadow: 'none' }}
              onFocus={(e) => e.target.style.boxShadow = 'none'}
            />
          </div>
        </div>
        <div>
          <button 
            className="btn btn-sm btn-primary"
            onClick={() => navigate('/secrets/new')}
          >
            <i className="fas fa-plus"></i> Create Secret
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">
                <i className="fas fa-key"></i> Secrets
                <span className="badge badge-primary ml-2" style={{ verticalAlign: 'middle' }}>
                  {filteredSecrets.length}{searchTerm ? ` of ${secrets.length}` : ''}
                </span>
              </h6>
            </div>
            <div className="card-body">

              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                  <div className="spinner-border" role="status" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>
                    <span className="visually-hidden"></span>
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Secret ID</th>
                        <th>Description</th>
                        <th>Tags</th>
                        <th>Type</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSecrets.map(secret => (
                        <tr 
                          key={secret.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/secrets/${secret.id}`)}
                        >
                          <td><strong>{secret.name}</strong></td>
                          <td><code className="text-muted">{secret.secret_id}</code></td>
                          <td>{secret.description || '-'}</td>
                          <td>
                            {secret.tags.length > 0 ? (
                              secret.tags.map(tag => (
                                <span key={tag} className="badge badge-secondary mr-1">{tag}</span>
                              ))
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <span className="badge badge-info">{secret.secret_type || 'General'}</span>
                          </td>
                          <td>
                            <small>{new Date(secret.created_at).toLocaleDateString()}</small>
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <DeleteButton
                              onDelete={() => handleDelete(secret.id)}
                              className="btn-sm"
                              title="Delete secret"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredSecrets.length === 0 && (
                    <div className="text-center text-muted py-4">
                      {searchTerm ? 'No secrets match your search' : 'No secrets found. Create your first secret to get started.'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecretsPage;
